import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Company } from '../../models/company';
import { CompanyService } from '../../services/company.service';
import { CampaignService } from '../../services/campaign.service';
import { RosterService } from '../../services/roster.service';
import { Campaign } from '../../models/campaign';
import { Contact } from '../../models/contact';
import { ContactService } from '../../services/contact.service';
import { DialogComponent } from '../dialog/dialog.component';

interface AccountPerformanceItem {
  accountId: string;
  accountName: string;
  totalRate: number;
  count: number;
  widthPercent: number;
  campaigns: { name: string; creatorName: string; rate: number }[];
}

interface BrandPerformanceItem {
  brandId: string;
  brandName: string;
  totalRate: number;
  count: number;
  widthPercent: number;
  campaigns: { name: string; creatorName: string; rate: number }[];
}

interface ContactPerformanceItem {
  contactId: string;
  contactName: string;
  totalRate: number;
  count: number;
  widthPercent: number;
  campaigns: { name: string; creatorName: string; rate: number }[];
}

type CompanyField = 'name' | 'type' | 'domains' | 'billTo' | 'billingInfo';
type ContactField = 'name' | 'email' | 'phoneNumber' | 'title' | 'role';
type ContactFilterKey =
  | 'contactNameFilter' | 'contactTitleFilter' | 'contactRoleFilter'
  | 'contactEmailFilter' | 'contactCompanyFilter';

@Component({
  selector: 'app-crm',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DialogComponent],
  templateUrl: './crm.component.html',
  styleUrl: './crm.component.css'
})
export class CrmComponent implements OnInit {
  activeTab = 'companies';

  // ── Companies (unified accounts + brands) ─────────────────────
  companies: Company[] = [];
  companiesLoaded = false;
  loadingCompanies = false;
  companiesError = '';
  companyFilter = '';
  companyTypeFilter: '' | 'Brand' | 'Agency' = '';

  readonly companyTypeOptions = ['Brand', 'Agency'];

  editingCompany: { companyId: string; field: CompanyField } | null = null;
  editingCompanyValue = '';
  editingCompanyOriginal = '';
  savingCompany = false;

  @ViewChild('companyEditor') companyEditorRef?: ElementRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

  deletingCompanyId: string | null = null;
  companyDeleteError = '';

  // Add Company dialog
  showAddCompanyDialog = false;
  newCompanyName = '';
  newCompanyType = 'Brand';
  newCompanyDomains = '';
  addingCompany = false;
  addCompanyError = '';

  // ── Account Performance (group campaigns by accountId role) ───
  accountPerformance: AccountPerformanceItem[] = [];
  performanceLoaded = false;
  loadingPerformance = false;
  performanceError = '';
  selectedAccountId: string | null = null;

  // ── Brand Performance (group campaigns by brandId role) ───────
  brandPerformance: BrandPerformanceItem[] = [];
  brandPerformanceLoaded = false;
  loadingBrandPerformance = false;
  brandPerformanceError = '';
  selectedBrandId: string | null = null;

  // ── Contacts ──────────────────────────────────────────────────
  contacts: Contact[] = [];
  contactsLoaded = false;
  loadingContacts = false;
  contactsError = '';
  contactNameFilter = '';
  contactTitleFilter = '';
  contactRoleFilter = '';
  contactEmailFilter = '';
  contactCompanyFilter = '';
  campaigns: Campaign[] = [];

  // Debounced contact filter input. filteredContacts is recomputed ONLY when the
  // debounce fires (or when the underlying contacts list changes) — never per keystroke.
  filteredContacts: Contact[] = [];
  private contactFilter$ = new Subject<{ key: ContactFilterKey; value: string }>();

  // Inline expand of a contact's linked campaigns (below the row)
  expandedContactId: string | null = null;
  expandedCampaigns: { createdDate: string; name: string; creatorName: string; rate: number }[] = [];

  readonly contactRoleOptions = ['Executive', 'CampaignCoordinator', 'Finance', 'Other'];

  // Name-resolution maps
  private companyNameMap = new Map<string, string>();
  private campaignNameMap = new Map<string, string>();    // campaignId -> campaign name
  private campaignBrandMap = new Map<string, string>();   // campaignId -> brandId (company id)
  private creatorNameMap = new Map<string, string>();     // creatorId (roster id) -> creator name

  editingContact: { contactId: string; field: ContactField } | null = null;
  editingContactValue = '';
  editingContactOriginal = '';
  savingContact = false;

  @ViewChild('contactEditor') contactEditorRef?: ElementRef<HTMLInputElement | HTMLSelectElement>;

  deletingContactId: string | null = null;
  contactDeleteError = '';

  // Add Contact dialog
  showAddContactDialog = false;
  newContactName = '';
  newContactEmail = '';
  newContactPhone = '';
  newContactTitle = '';
  newContactRole = '';
  newContactCompanyId = '';
  addingContact = false;
  addContactError = '';

  // Manage Links dialog
  showLinksDialog = false;
  linksContact: Contact | null = null;
  linkCampaignToAdd = '';
  savingLink = false;
  linkError = '';

  // Contact Performance
  contactPerformance: ContactPerformanceItem[] = [];
  contactPerformanceLoaded = false;
  loadingContactPerformance = false;
  contactPerformanceError = '';
  selectedContactId: string | null = null;

  constructor(
    private companyService: CompanyService,
    private campaignService: CampaignService,
    private rosterService: RosterService,
    private contactService: ContactService
  ) {}

  ngOnInit(): void {
    this.loadCompanies();
    this.contactFilter$.pipe(debounceTime(300)).subscribe(({ key, value }) => {
      this[key] = value;
      this.applyContactFilters();
    });
  }

  onContactFilterChange(key: ContactFilterKey, value: string): void {
    this.contactFilter$.next({ key, value });
  }

  setTab(tab: string): void {
    this.activeTab = tab;
    this.companyDeleteError = '';
    this.contactDeleteError = '';
    if (tab === 'companies' && !this.companiesLoaded) { this.loadCompanies(); }
    if (tab === 'performance' && !this.performanceLoaded) { this.loadPerformance(); }
    if (tab === 'brandPerformance' && !this.brandPerformanceLoaded) { this.loadBrandPerformance(); }
    if (tab === 'contacts' && !this.contactsLoaded) { this.loadContacts(); }
    if (tab === 'contactPerformance' && !this.contactPerformanceLoaded) { this.loadContactPerformance(); }
  }

  // ── Companies ─────────────────────────────────────────────────
  private setCompanies(data: Company[]): void {
    this.companies = [...data].sort((a, b) => a.name.localeCompare(b.name));
    this.companyNameMap = new Map(this.companies.map(c => [c.id, c.name]));
  }

  loadCompanies(): void {
    this.loadingCompanies = true;
    this.companiesError = '';
    this.companyService.getCompanies().subscribe({
      next: (data) => {
        this.setCompanies(data);
        this.companiesLoaded = true;
        this.loadingCompanies = false;
      },
      error: (error) => {
        this.companiesError = 'Failed to load companies';
        console.error('Error loading companies:', error);
        this.loadingCompanies = false;
      }
    });
  }

  get filteredCompanies(): Company[] {
    const q = this.companyFilter.trim().toLowerCase();
    return this.companies.filter(c =>
      (!this.companyTypeFilter || c.type === this.companyTypeFilter) &&
      (!q || c.name.toLowerCase().includes(q))
    );
  }

  companyName(id: string | null | undefined): string {
    if (!id) return '';
    return this.companyNameMap.get(id) ?? id;
  }

  isCompanyTextarea(field: CompanyField): boolean {
    return field === 'billTo' || field === 'billingInfo';
  }

  isEditingCompany(company: Company, field: CompanyField): boolean {
    return this.editingCompany?.companyId === company.id && this.editingCompany?.field === field;
  }

  startCompanyEdit(company: Company, field: CompanyField): void {
    if (this.editingCompany) return;
    const current = (company[field] ?? '') as string;
    this.editingCompanyOriginal = current;
    this.editingCompanyValue = current;
    this.editingCompany = { companyId: company.id, field };
    setTimeout(() => {
      const el = this.companyEditorRef?.nativeElement;
      if (el) { el.focus(); (el as HTMLInputElement).select?.(); }
    }, 0);
  }

  cancelCompanyEdit(): void {
    this.editingCompany = null;
    this.editingCompanyValue = '';
    this.editingCompanyOriginal = '';
  }

  commitCompanyEdit(): void {
    if (!this.editingCompany || this.savingCompany) return;
    const { companyId, field } = this.editingCompany;
    const newValue = this.editingCompanyValue;

    if (newValue === this.editingCompanyOriginal) { this.cancelCompanyEdit(); return; }

    this.savingCompany = true;
    this.companyService.patchCompanyField(companyId, field, newValue).subscribe({
      next: (updated) => {
        const idx = this.companies.findIndex(c => c.id === companyId);
        if (idx >= 0) {
          this.companies[idx] = updated ? { ...this.companies[idx], ...updated } : { ...this.companies[idx], [field]: newValue };
          this.companyNameMap.set(companyId, this.companies[idx].name);
        }
        this.savingCompany = false;
        this.editingCompany = null;
        this.editingCompanyValue = '';
        this.editingCompanyOriginal = '';
      },
      error: (err) => {
        console.error('Failed to update company field', field, err);
        this.savingCompany = false;
      }
    });
  }

  onCompanyEditKeydown(event: KeyboardEvent): void {
    if (!this.editingCompany) return;
    const isTextarea = this.isCompanyTextarea(this.editingCompany.field);
    if (event.key === 'Escape') { event.preventDefault(); this.cancelCompanyEdit(); return; }
    if (event.key === 'Enter') {
      if (isTextarea) { if (event.ctrlKey || event.metaKey) { event.preventDefault(); this.commitCompanyEdit(); } }
      else { event.preventDefault(); this.commitCompanyEdit(); }
    }
  }

  deleteCompany(company: Company): void {
    if (this.deletingCompanyId) return;
    this.companyDeleteError = '';
    this.deletingCompanyId = company.id;
    this.companyService.deleteCompany(company.id).subscribe({
      next: () => {
        this.companies = this.companies.filter(c => c.id !== company.id);
        this.companyNameMap.delete(company.id);
        this.deletingCompanyId = null;
      },
      error: (err) => {
        this.deletingCompanyId = null;
        this.companyDeleteError = err?.status === 409
          ? 'Delete failed. You cannot delete a company that is linked to a campaign.'
          : 'Failed to delete company.';
      }
    });
  }

  openAddCompanyDialog(): void {
    this.newCompanyName = '';
    this.newCompanyType = 'Brand';
    this.newCompanyDomains = '';
    this.addCompanyError = '';
    this.showAddCompanyDialog = true;
  }

  addCompany(): void {
    const name = this.newCompanyName.trim();
    if (!name || this.addingCompany) return;

    this.addingCompany = true;
    this.addCompanyError = '';
    this.companyService.addCompany({
      name,
      type: this.newCompanyType,
      domains: this.newCompanyDomains.trim(),
      billTo: '',
      billingInfo: ''
    }).subscribe({
      next: (created) => {
        this.companies = [...this.companies, created].sort((a, b) => a.name.localeCompare(b.name));
        this.companyNameMap.set(created.id, created.name);
        this.companiesLoaded = true;
        this.addingCompany = false;
        this.showAddCompanyDialog = false;
      },
      error: (err) => {
        console.error('Failed to add company', err);
        this.addCompanyError = 'Failed to add company.';
        this.addingCompany = false;
      }
    });
  }

  // ── Performance helpers ───────────────────────────────────────
  toggleAccount(accountId: string): void {
    this.selectedAccountId = this.selectedAccountId === accountId ? null : accountId;
  }

  toggleBrand(brandId: string): void {
    this.selectedBrandId = this.selectedBrandId === brandId ? null : brandId;
  }

  loadPerformance(): void {
    this.loadingPerformance = true;
    this.performanceError = '';

    forkJoin({
      campaigns: this.campaignService.getCampaigns(),
      companies: this.companyService.getCompanies(),
      rosters: this.rosterService.getRosters()
    }).subscribe({
      next: ({ campaigns, companies, rosters }) => {
        const companyMap = new Map<string, string>(companies.map(c => [c.id, c.name]));
        const creatorMap = new Map<string, string>(rosters.map(r => [r.id, r.name]));

        const grouped = new Map<string, { totalRate: number; campaigns: Campaign[] }>();
        for (const c of campaigns) {
          if (!c.accountId || c.deleted) continue;
          const existing = grouped.get(c.accountId);
          if (existing) { existing.totalRate += c.rate ?? 0; existing.campaigns.push(c); }
          else { grouped.set(c.accountId, { totalRate: c.rate ?? 0, campaigns: [c] }); }
        }

        const items: AccountPerformanceItem[] = [];
        grouped.forEach((stats, accountId) => {
          const sortedCampaigns = [...stats.campaigns].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
          items.push({
            accountId,
            accountName: companyMap.get(accountId) ?? accountId,
            totalRate: stats.totalRate,
            count: stats.campaigns.length,
            widthPercent: 0,
            campaigns: sortedCampaigns.map(c => ({
              name: c.name,
              creatorName: creatorMap.get(c.creatorId) ?? c.creatorName ?? '—',
              rate: c.rate ?? 0
            }))
          });
        });

        items.sort((a, b) => b.totalRate - a.totalRate);
        const maxRate = items[0]?.totalRate ?? 1;
        items.forEach(item => { item.widthPercent = (item.totalRate / maxRate) * 100; });

        this.accountPerformance = items;
        this.performanceLoaded = true;
        this.loadingPerformance = false;
      },
      error: (error) => {
        this.performanceError = 'Failed to load account performance data';
        console.error('Error loading performance data:', error);
        this.loadingPerformance = false;
      }
    });
  }

  loadBrandPerformance(): void {
    this.loadingBrandPerformance = true;
    this.brandPerformanceError = '';

    forkJoin({
      campaigns: this.campaignService.getCampaigns(),
      companies: this.companyService.getCompanies(),
      rosters: this.rosterService.getRosters()
    }).subscribe({
      next: ({ campaigns, companies, rosters }) => {
        const companyMap = new Map<string, string>(companies.map(c => [c.id, c.name]));
        const creatorMap = new Map<string, string>(rosters.map(r => [r.id, r.name]));

        const grouped = new Map<string, { totalRate: number; campaigns: Campaign[] }>();
        for (const c of campaigns) {
          if (!c.brandId || c.deleted) continue;
          const existing = grouped.get(c.brandId);
          if (existing) { existing.totalRate += c.rate ?? 0; existing.campaigns.push(c); }
          else { grouped.set(c.brandId, { totalRate: c.rate ?? 0, campaigns: [c] }); }
        }

        const items: BrandPerformanceItem[] = [];
        grouped.forEach((stats, brandId) => {
          const sortedCampaigns = [...stats.campaigns].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
          items.push({
            brandId,
            brandName: companyMap.get(brandId) ?? brandId,
            totalRate: stats.totalRate,
            count: stats.campaigns.length,
            widthPercent: 0,
            campaigns: sortedCampaigns.map(c => ({
              name: c.name,
              creatorName: creatorMap.get(c.creatorId) ?? c.creatorName ?? '—',
              rate: c.rate ?? 0
            }))
          });
        });

        items.sort((a, b) => b.totalRate - a.totalRate);
        const maxRate = items[0]?.totalRate ?? 1;
        items.forEach(item => { item.widthPercent = (item.totalRate / maxRate) * 100; });

        this.brandPerformance = items;
        this.brandPerformanceLoaded = true;
        this.loadingBrandPerformance = false;
      },
      error: (error) => {
        this.brandPerformanceError = 'Failed to load brand performance data';
        console.error('Error loading brand performance data:', error);
        this.loadingBrandPerformance = false;
      }
    });
  }

  // ── Contacts: load ────────────────────────────────────────────
  loadContacts(): void {
    this.loadingContacts = true;
    this.contactsError = '';
    forkJoin({
      contacts: this.contactService.getContacts(),
      companies: this.companyService.getCompanies(),
      campaigns: this.campaignService.getCampaigns(),
      rosters: this.rosterService.getRosters()
    }).subscribe({
      next: ({ contacts, companies, campaigns, rosters }) => {
        this.contacts = [...contacts].sort((a, b) => a.name.localeCompare(b.name));
        this.applyContactFilters();
        this.setCompanies(companies);
        this.campaigns = [...campaigns].filter(c => !c.deleted).sort((a, b) => a.name.localeCompare(b.name));

        this.campaignNameMap = new Map(this.campaigns.map(c => [c.id, c.name]));
        this.campaignBrandMap = new Map(this.campaigns.filter(c => !!c.brandId).map(c => [c.id, c.brandId as string]));
        this.creatorNameMap = new Map(rosters.map(r => [r.id, r.name]));

        this.contactsLoaded = true;
        this.loadingContacts = false;
      },
      error: (error) => {
        this.contactsError = 'Failed to load contacts';
        console.error('Error loading contacts:', error);
        this.loadingContacts = false;
      }
    });
  }

  applyContactFilters(): void {
    const name = this.contactNameFilter.trim().toLowerCase();
    const title = this.contactTitleFilter.trim().toLowerCase();
    const role = this.contactRoleFilter.trim().toLowerCase();
    const email = this.contactEmailFilter.trim().toLowerCase();
    const company = this.contactCompanyFilter.trim().toLowerCase();
    this.filteredContacts = this.contacts.filter(c =>
      (!name || (c.name ?? '').toLowerCase().includes(name)) &&
      (!title || (c.title ?? '').toLowerCase().includes(title)) &&
      (!role || this.roleLabel(c.role ?? '').toLowerCase().includes(role)) &&
      (!email || (c.email ?? '').toLowerCase().includes(email)) &&
      (!company || this.companyLabel(c).toLowerCase().includes(company))
    );
  }

  get contactFiltersActive(): boolean {
    return !!(this.contactNameFilter || this.contactTitleFilter || this.contactRoleFilter ||
      this.contactEmailFilter || this.contactCompanyFilter);
  }

  campaignName(id: string): string { return this.campaignNameMap.get(id) ?? id; }

  /** Resolve a contact's employer/home company to a display name. */
  companyLabel(contact: Contact): string {
    return this.companyName(contact.companyId);
  }

  /**
   * Brands a contact has actually worked with — DERIVED (not stored) as the distinct
   * brandIds (company ids) across the contact's campaigns. Safe campaign→brand direction.
   */
  derivedBrandNames(contact: Contact): string[] {
    const brandIds = new Set<string>();
    for (const cid of contact.campaignIds ?? []) {
      const bid = this.campaignBrandMap.get(cid);
      if (bid) brandIds.add(bid);
    }
    return [...brandIds].map(id => this.companyName(id)).sort((a, b) => a.localeCompare(b));
  }

  roleLabel(role: string): string {
    if (!role) return '';
    return role === 'CampaignCoordinator' ? 'Campaign Coordinator' : role;
  }

  // ── Contacts: inline edit ─────────────────────────────────────
  isEditingContact(contact: Contact, field: ContactField): boolean {
    return this.editingContact?.contactId === contact.id && this.editingContact?.field === field;
  }

  startContactEdit(contact: Contact, field: ContactField): void {
    if (this.editingContact) return;
    const current = (contact[field] ?? '') as string;
    this.editingContactOriginal = current;
    this.editingContactValue = current;
    this.editingContact = { contactId: contact.id, field };
    setTimeout(() => {
      const el = this.contactEditorRef?.nativeElement;
      if (el) { el.focus(); (el as HTMLInputElement).select?.(); }
    }, 0);
  }

  cancelContactEdit(): void {
    this.editingContact = null;
    this.editingContactValue = '';
    this.editingContactOriginal = '';
  }

  commitContactEdit(): void {
    if (!this.editingContact || this.savingContact) return;
    const { contactId, field } = this.editingContact;
    const newValue = this.editingContactValue;

    if (newValue === this.editingContactOriginal) { this.cancelContactEdit(); return; }

    this.savingContact = true;
    this.contactService.patchContactField(contactId, field, newValue).subscribe({
      next: (updated) => {
        const idx = this.contacts.findIndex(c => c.id === contactId);
        if (idx >= 0) {
          this.contacts[idx] = updated ? { ...this.contacts[idx], ...updated } : { ...this.contacts[idx], [field]: newValue };
          this.applyContactFilters();
        }
        this.savingContact = false;
        this.editingContact = null;
        this.editingContactValue = '';
        this.editingContactOriginal = '';
      },
      error: (err) => {
        console.error('Failed to update contact field', field, err);
        this.savingContact = false;
      }
    });
  }

  onContactEditKeydown(event: KeyboardEvent): void {
    if (!this.editingContact) return;
    if (event.key === 'Escape') { event.preventDefault(); this.cancelContactEdit(); return; }
    if (event.key === 'Enter') { event.preventDefault(); this.commitContactEdit(); }
  }

  // ── Contacts: delete ──────────────────────────────────────────
  deleteContact(contact: Contact): void {
    if (this.deletingContactId) return;
    this.contactDeleteError = '';
    this.deletingContactId = contact.id;
    this.contactService.deleteContact(contact.id).subscribe({
      next: () => {
        this.contacts = this.contacts.filter(c => c.id !== contact.id);
        this.applyContactFilters();
        this.deletingContactId = null;
      },
      error: (err) => {
        console.error('Failed to delete contact', err);
        this.deletingContactId = null;
        this.contactDeleteError = 'Failed to delete contact.';
      }
    });
  }

  // ── Add Contact ───────────────────────────────────────────────
  openAddContactDialog(): void {
    this.newContactName = '';
    this.newContactEmail = '';
    this.newContactPhone = '';
    this.newContactTitle = '';
    this.newContactRole = '';
    this.newContactCompanyId = '';
    this.addContactError = '';
    this.showAddContactDialog = true;
  }

  addContact(): void {
    const name = this.newContactName.trim();
    if (!name || this.addingContact) return;

    this.addingContact = true;
    this.addContactError = '';
    this.contactService.addContact({
      name,
      email: this.newContactEmail.trim(),
      phoneNumber: this.newContactPhone.trim(),
      title: this.newContactTitle.trim(),
      role: this.newContactRole,
      companyId: this.newContactCompanyId
    }).subscribe({
      next: (created) => {
        this.contacts = [...this.contacts, created].sort((a, b) => a.name.localeCompare(b.name));
        this.applyContactFilters();
        this.contactsLoaded = true;
        this.addingContact = false;
        this.showAddContactDialog = false;
      },
      error: (err) => {
        console.error('Failed to add contact', err);
        this.addContactError = 'Failed to add contact.';
        this.addingContact = false;
      }
    });
  }

  // ── Inline campaign expand (below the contact row) ────────────
  /**
   * Number of the contact's linked campaigns that actually resolve to a
   * known, non-deleted campaign. Keeps the Campaigns-column count in sync
   * with the expanded list (which drops links to deleted/missing campaigns).
   */
  linkedCampaignCount(contact: Contact): number {
    return (contact.campaignIds ?? []).filter(id => this.campaignNameMap.has(id)).length;
  }

  toggleContactCampaigns(contact: Contact): void {
    if (this.expandedContactId === contact.id) {
      this.expandedContactId = null;
      this.expandedCampaigns = [];
      return;
    }
    const byId = new Map(this.campaigns.map(c => [c.id, c]));
    this.expandedCampaigns = (contact.campaignIds ?? [])
      .map(id => byId.get(id))
      .filter((c): c is Campaign => !!c)
      .sort((a, b) => (b.createdDate ?? '').localeCompare(a.createdDate ?? ''))
      .map(c => ({
        createdDate: c.createdDate,
        name: c.name,
        creatorName: this.creatorNameMap.get(c.creatorId) ?? c.creatorName ?? '—',
        rate: c.rate ?? 0
      }));
    this.expandedContactId = contact.id;
  }

  formatDate(value: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd = d.getDate().toString().padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }

  // ── Manage Links dialog ───────────────────────────────────────
  openLinksDialog(contact: Contact): void {
    this.linksContact = contact;
    this.linkCampaignToAdd = '';
    this.linkError = '';
    this.showLinksDialog = true;
  }

  /** Set (or clear) the contact's employer company from the dropdown value (a company id). */
  setEmployer(companyId: string): void {
    if (!this.linksContact || this.savingLink) return;
    this.patchLinkField('companyId', companyId || null);
  }

  availableCampaigns(): Campaign[] {
    const linked = new Set(this.linksContact?.campaignIds ?? []);
    return this.campaigns.filter(c => !linked.has(c.id));
  }

  addCampaignLink(): void {
    if (!this.linksContact || !this.linkCampaignToAdd) return;
    const next = [...(this.linksContact.campaignIds ?? []), this.linkCampaignToAdd];
    this.patchLinkField('campaignIds', next);
    this.linkCampaignToAdd = '';
  }

  removeCampaignLink(id: string): void {
    if (!this.linksContact) return;
    const next = (this.linksContact.campaignIds ?? []).filter(c => c !== id);
    this.patchLinkField('campaignIds', next);
  }

  private patchLinkField(field: 'companyId' | 'campaignIds', value: string | string[] | null): void {
    if (!this.linksContact || this.savingLink) return;
    const contactId = this.linksContact.id;
    this.savingLink = true;
    this.linkError = '';
    this.contactService.patchContactField(contactId, field, value).subscribe({
      next: (updated) => {
        const idx = this.contacts.findIndex(c => c.id === contactId);
        if (idx >= 0) {
          this.contacts[idx] = updated ? { ...this.contacts[idx], ...updated } : { ...this.contacts[idx], [field]: value as any };
          this.linksContact = this.contacts[idx];
          this.applyContactFilters();
        }
        this.savingLink = false;
      },
      error: (err) => {
        console.error('Failed to update contact links', field, err);
        this.linkError = 'Failed to update links.';
        this.savingLink = false;
      }
    });
  }

  // ── Contact Performance ───────────────────────────────────────
  toggleContact(contactId: string): void {
    this.selectedContactId = this.selectedContactId === contactId ? null : contactId;
  }

  loadContactPerformance(): void {
    this.loadingContactPerformance = true;
    this.contactPerformanceError = '';

    forkJoin({
      campaigns: this.campaignService.getCampaigns(),
      contacts: this.contactService.getContacts(),
      rosters: this.rosterService.getRosters()
    }).subscribe({
      next: ({ campaigns, contacts, rosters }) => {
        const campaignById = new Map<string, Campaign>(campaigns.map(c => [c.id, c]));
        const creatorMap = new Map<string, string>(rosters.map(r => [r.id, r.name]));

        const items: ContactPerformanceItem[] = [];
        for (const contact of contacts) {
          // Credit is based on the explicit contact↔campaign link only (no over-crediting).
          const linked = (contact.campaignIds ?? [])
            .map(id => campaignById.get(id))
            .filter((c): c is Campaign => !!c && !c.deleted);
          if (linked.length === 0) continue;

          const totalRate = linked.reduce((sum, c) => sum + (c.rate ?? 0), 0);
          const sorted = [...linked].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
          items.push({
            contactId: contact.id,
            contactName: contact.name || contact.email || contact.id,
            totalRate,
            count: linked.length,
            widthPercent: 0,
            campaigns: sorted.map(c => ({
              name: c.name,
              creatorName: creatorMap.get(c.creatorId) ?? c.creatorName ?? '—',
              rate: c.rate ?? 0
            }))
          });
        }

        items.sort((a, b) => b.totalRate - a.totalRate);
        const maxRate = items[0]?.totalRate ?? 1;
        items.forEach(item => { item.widthPercent = (item.totalRate / maxRate) * 100; });

        this.contactPerformance = items;
        this.contactPerformanceLoaded = true;
        this.loadingContactPerformance = false;
      },
      error: (error) => {
        this.contactPerformanceError = 'Failed to load contact performance data';
        console.error('Error loading contact performance data:', error);
        this.loadingContactPerformance = false;
      }
    });
  }

  formatRate(rate: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(rate);
  }
}
